import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import { useCoinStore } from '../stores/coinStore';
import { useTenantStore } from '../stores/tenantStore';
import PcMenu from './Sidebar/PcMenu';
import MobileMenu from './Sidebar/MobileMenu';
import { useDialog } from '../hooks/useDialog';
import { getMenuItems } from '../config/app.config';
import { useTranslation } from 'react-i18next';

const Layout: React.FC = () => {
  const { t } = useTranslation(['nav', 'app']);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuthStore();
  const { account, loadAccount } = useCoinStore();
  const { confirm } = useDialog();
  const { name: siteName, slogan: siteSlogan } = useTenantStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current route key from pathname
  const getCurrentKey = () => {
    const path = location.pathname.replace('/', '');
    // music-edit 归属于音乐模块，高亮 music-generation
    if (path === 'music-edit') return 'music-generation';
    return path || 'dashboard';
  };

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const handleLogout = async () => {
    const confirmed = await confirm(t('nav:userMenu.logoutConfirm'), t('nav:userMenu.logoutTitle'), 'warning');
    if (confirmed) {
      logout();
      navigate('/login');
    }
  };

  const handleMenuClick = (key: string) => {
    navigate(`/${key}`);
  };

  return (
    <div className="flex h-screen font-sans bg-bg-page">
      {!isMobile && (
        <PcMenu
          menuItems={getMenuItems(t)}
          selectedKey={getCurrentKey()}
          userName={user?.name}
          userRole={user?.role}
          coinBalance={account?.balance ?? 0}
          onMenuClick={handleMenuClick}
          onLogout={handleLogout}
          onProfileClick={() => navigate('/profile')}
          onCoinsClick={() => navigate('/coins')}
        />
      )}

      <main className="flex-1 flex flex-col min-w-0">
        {isMobile && (
          <header className="bg-bg-surface border-b border-border-subtle px-4 py-3 flex items-center justify-between h-16 shadow-sm">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-bg-elevated hover:text-white text-gray-400 transition-all"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center shadow-md">
                <Squares2X2Icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-bold text-gray-50 font-serif">
                  {siteName}
                </span>
                <span className="text-xs text-gray-400 font-medium leading-none">
                  {siteSlogan}
                </span>
              </div>
            </div>
            <div className="w-10"></div>
          </header>
        )}

        <div className="flex-1 overflow-auto bg-bg-page">
          <Outlet />
        </div>
      </main>

      {isMobile && (
        <MobileMenu
          visible={mobileMenuOpen}
          menuItems={getMenuItems(t)}
          selectedKey={getCurrentKey()}
          userName={user?.name}
          userRole={user?.role}
          onClose={() => setMobileMenuOpen(false)}
          onMenuClick={handleMenuClick}
          onProfileClick={() => navigate('/profile')}
          onCoinsClick={() => navigate('/coins')}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export default Layout;
